import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft, Plus, RefreshCw, Loader2, Trash2, PoundSterling,
  FileText, Home, Flame, Zap, FileCheck, Wrench, Shield, ClipboardList
} from "lucide-react";
import { API } from "@/App";

const SERVICE_TYPES = [
  { value: "letting_fee", label: "Letting Fee", icon: FileText, xeroCode: "202" },
  { value: "inventory_checkin", label: "Inventory Check-In", icon: ClipboardList, xeroCode: "204" },
  { value: "inventory_checkout", label: "Inventory Check-Out", icon: ClipboardList, xeroCode: "204" },
  { value: "inventory_interim", label: "Interim Inspection", icon: ClipboardList, xeroCode: "204" },
  { value: "gas_safety", label: "Gas Safety Certificate", icon: Flame, xeroCode: "208" },
  { value: "eicr", label: "EICR Certificate", icon: Zap, xeroCode: "208" },
  { value: "epc", label: "EPC Certificate", icon: FileCheck, xeroCode: "208" },
  { value: "boiler_service", label: "Boiler Service", icon: Wrench, xeroCode: "210" },
  { value: "rent_guarantee", label: "Rent Guarantee Insurance", icon: Shield, xeroCode: "206" },
  { value: "other", label: "Other Service", icon: FileText, xeroCode: "210" }
];

export const LandlordServicesPage = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  
  const [loading, setLoading] = useState(true);
  const [services, setServices] = useState([]);
  const [properties, setProperties] = useState([]);
  const [landlords, setLandlords] = useState([]);
  const [summary, setSummary] = useState({});
  
  // Add service modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    property_id: "",
    landlord_id: "",
    service_type: "",
    description: "",
    amount: "",
    notes: ""
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [servicesRes, propertiesRes, landlordsRes, summaryRes] = await Promise.all([
        axios.get(`${API}/landlord-services?limit=100`, { headers }),
        axios.get(`${API}/lettings/properties`, { headers }),
        axios.get(`${API}/lettings/landlords`, { headers }),
        axios.get(`${API}/landlord-services/summary`, { headers })
      ]);

      setServices(servicesRes.data.services || []);
      setProperties(propertiesRes.data.properties || []);
      setLandlords(landlordsRes.data.landlords || []);
      setSummary(summaryRes.data.summary || {});
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handlePropertyChange = (propertyId) => {
    setFormData({ ...formData, property_id: propertyId });
    
    // Auto-fill landlord if property has one
    const property = properties.find(p => p.property_id === propertyId);
    if (property?.landlord_id) {
      setFormData(prev => ({ ...prev, property_id: propertyId, landlord_id: property.landlord_id }));
    }
  };

  const handleAddService = async () => {
    if (!formData.property_id || !formData.landlord_id || !formData.service_type || !formData.amount) {
      toast.error("Please fill all required fields");
      return;
    }

    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const serviceType = SERVICE_TYPES.find(s => s.value === formData.service_type);
      
      await axios.post(`${API}/landlord-services`, {
        property_id: formData.property_id,
        landlord_id: formData.landlord_id,
        service_type: formData.service_type,
        description: formData.description || serviceType?.label || "Service charge",
        amount: parseFloat(formData.amount),
        notes: formData.notes
      }, { headers });

      toast.success("Service charge added successfully");
      setShowAddModal(false);
      setFormData({
        property_id: "",
        landlord_id: "",
        service_type: "",
        description: "",
        amount: "",
        notes: ""
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add service");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteService = async (serviceId) => {
    if (!confirm("Delete this service charge?")) return;
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/landlord-services/${serviceId}`, { headers });
      toast.success("Service deleted");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete");
    }
  };

  const getServiceIcon = (type) => {
    const service = SERVICE_TYPES.find(s => s.value === type);
    const Icon = service?.icon || FileText;
    return <Icon className="w-4 h-4" />;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-14 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => navigate("/accounts")}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold text-slate-800">Landlord Services</h1>
                <p className="text-xs text-muted-foreground">Fees, charges & invoices</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" size="sm" onClick={fetchData}>
                <RefreshCw className="w-4 h-4" />
              </Button>
              <Button onClick={() => setShowAddModal(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Add Charge
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 mb-8">
          {SERVICE_TYPES.slice(0, 5).map(type => {
            const data = summary[type.value] || { count: 0, total_amount: 0, pending_sync: 0 };
            const Icon = type.icon;
            return (
              <Card key={type.value}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon className="w-4 h-4 text-slate-500" />
                    <span className="text-xs font-medium text-slate-600">{type.label}</span>
                  </div>
                  <p className="text-xl font-bold">£{(data.total_amount || 0).toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground">
                    {data.count || 0} charges • {data.pending_sync || 0} pending
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Services List */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Recent Charges</CardTitle>
            <CardDescription>All landlord service charges and fees</CardDescription>
          </CardHeader>
          <CardContent>
            {services.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-muted-foreground">No service charges yet</p>
                <Button onClick={() => setShowAddModal(true)} className="mt-4">
                  <Plus className="w-4 h-4 mr-2" />
                  Add First Charge
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {services.map(service => (
                  <div 
                    key={service.service_id}
                    className="flex items-center justify-between p-4 bg-slate-50 rounded-lg"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-lg bg-white border flex items-center justify-center">
                        {getServiceIcon(service.service_type)}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium">{service.service_name}</p>
                          <Badge variant="outline" className="text-xs">
                            {service.xero_code}
                          </Badge>
                          {service.xero_synced ? (
                            <Badge className="bg-green-100 text-green-700 text-xs">Synced</Badge>
                          ) : (
                            <Badge variant="secondary" className="text-xs">Pending</Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {service.property_address} • {service.landlord_name}
                        </p>
                        {service.description && (
                          <p className="text-xs text-muted-foreground mt-1">{service.description}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="font-semibold">£{service.amount?.toFixed(2)}</p>
                        <p className="text-xs text-muted-foreground">
                          +£{service.vat_amount?.toFixed(2)} VAT
                        </p>
                      </div>
                      {!service.xero_synced && (
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleDeleteService(service.service_id)}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Add Service Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Service Charge</DialogTitle>
            <DialogDescription>
              Create a new landlord service charge or fee
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Property *</Label>
              <Select
                value={formData.property_id}
                onValueChange={handlePropertyChange}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select property" />
                </SelectTrigger>
                <SelectContent>
                  {properties.map(prop => (
                    <SelectItem key={prop.property_id} value={prop.property_id}>
                      {prop.address}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Landlord *</Label>
              <Select
                value={formData.landlord_id}
                onValueChange={(v) => setFormData({ ...formData, landlord_id: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select landlord" />
                </SelectTrigger>
                <SelectContent>
                  {landlords.map(ll => (
                    <SelectItem key={ll.landlord_id} value={ll.landlord_id}>
                      {ll.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Service Type *</Label>
              <Select
                value={formData.service_type}
                onValueChange={(v) => {
                  const serviceType = SERVICE_TYPES.find(s => s.value === v);
                  setFormData({ 
                    ...formData, 
                    service_type: v,
                    description: serviceType?.label || ""
                  });
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select service type" />
                </SelectTrigger>
                <SelectContent>
                  {SERVICE_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      <div className="flex items-center gap-2">
                        <type.icon className="w-4 h-4" />
                        {type.label}
                        <span className="text-muted-foreground">({type.xeroCode})</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Description</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Service description"
              />
            </div>

            <div>
              <Label>Amount (excl. VAT) *</Label>
              <div className="relative">
                <PoundSterling className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  className="pl-9"
                  placeholder="0.00"
                />
              </div>
              {formData.amount && (
                <p className="text-xs text-muted-foreground mt-1">
                  VAT (20%): £{(parseFloat(formData.amount) * 0.2).toFixed(2)} • 
                  Total: £{(parseFloat(formData.amount) * 1.2).toFixed(2)}
                </p>
              )}
            </div>

            <div>
              <Label>Notes (optional)</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Any additional notes"
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddService} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Add Charge
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LandlordServicesPage;
